import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { EnhanceMessagePromptType, MessageContentAttachmentMediaTypeEnum } from '@ffd/api-client';
import {
  AudioInputDropdownComponent,
  IfRoleDirective,
  pathParameter,
  ScreenRecordingService,
  SecondsToTimePipe,
  TagPusherService,
  TagEvent,
} from '@libs/shared-services';
import { ContextMenuHeaderButton, GenerateTextAction, VoiceTypingTextFieldComponent } from '@libs/ui-lib';
import { imageMimeTypes, Nullable, videoMimeTypes } from '@libs/utils';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ActivatedRoute } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { destructureFullSeries, FullSeriesPreviewComponent, FullSeriesVM, ImagingGalleryItem } from '@libs/imaging';
import { PreviewImageModule, PreviewVideoModule } from '@libs/media-preview';
import { ActiveTranscriptionService } from '@libs/speech-recognition';
import { filter, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  CollaborationImageSelectorDialogComponent,
  CollaborationImageSelectorDialogData,
  CollaborationImageSelectorDialogResult,
} from '../collaboration-image-selector-dialog';
import { CollabSidebarElement } from '../collab-sidebar';
import { AttachmentComponent } from '../attachment';
import { CollabMessageAttachment } from '../message';
import { MessageInputService } from './message-input.service';
import { MessageInputVM } from './message-input.vm';

const TOOLBAR_OPEN_DELAY = 500;

@Component({
  selector: 'lib-message-input',
  templateUrl: './message-input.component.html',
  styleUrls: ['./message-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    SvgIconComponent,
    PreviewImageModule,
    PreviewVideoModule,
    LottieComponent,
    IfRoleDirective,
    MatMenuModule,
    SecondsToTimePipe,
    FullSeriesPreviewComponent,
    AttachmentComponent,
    AudioInputDropdownComponent,
    VoiceTypingTextFieldComponent,
  ],
})
export class MessageInputComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly screenRecordingService = inject(ScreenRecordingService);
  private readonly activeTranscriptionService = inject(ActiveTranscriptionService);
  private readonly messageInputService = inject(MessageInputService);
  private readonly dialog = inject(Dialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageChangeSubject$ = new Subject<string>();
  private readonly tagPusherService = inject(TagPusherService);

  @Input() public hideUploadFromGallery = false;
  @Input() public set selectedCollaboration(selectedCollaboration: CollabSidebarElement) {
    this.siteId.set(selectedCollaboration.siteId);
    this.patientId.set(selectedCollaboration.linkedPatient?.id);
  }

  @Output() public readonly fileUpload = new EventEmitter<File[]>();
  @Output() public readonly send = new EventEmitter<MessageInputVM>();
  @Output() public readonly recordClick = new EventEmitter<void>();
  @Output() public readonly toggleDictation = new EventEmitter<void>();
  @Output() public readonly selectDevice = new EventEmitter<string>();
  @Output() public readonly messageTyping = new EventEmitter<boolean>();

  @ViewChild('menu', { static: true, read: ElementRef }) private readonly menuElement!: ElementRef<HTMLDivElement>;
  @ViewChild('imageUploader', { static: true, read: ElementRef }) private readonly imageUploaderElement!: ElementRef<HTMLInputElement>;
  @ViewChild('voiceTypingTextField', { static: true }) public voiceTypingTextField!: VoiceTypingTextFieldComponent;

  protected siteId = signal<Nullable<string>>(null);
  protected patientId = signal<Nullable<string>>(null);
  protected attachmentsSignal = computed(() => this.messageInputService.viewModel().attachments);
  protected isMenuOpen = signal(false);
  public recordCounter = this.screenRecordingService.recordCounter;
  public readonly transcription = this.activeTranscriptionService.transcriptionControl;
  public readonly transcriptionStatus = this.activeTranscriptionService.status;
  public readonly transcriptionStatus$ = toObservable(this.activeTranscriptionService.status);
  public readonly activeDeviceId = this.activeTranscriptionService.activeDeviceId;
  public readonly isEnhanceToolbarOpen = signal(false);
  protected readonly isGeneratingSignal = computed(() => this.messageInputService.viewModel().isGenerating);
  protected readonly isUndoDisabledSignal = this.messageInputService.isUndoDisabledSignal;
  protected readonly isRedoDisabledSignal = this.messageInputService.isRedoDisabledSignal;
  protected selectedEnhanceButton = signal<Nullable<EnhanceMessagePromptType>>(null);
  public lastFocusedCursorOffsetSignal = this.messageInputService.lastFocusedCursorOffsetSignal;
  public voiceTypingTextFieldHasText = signal<boolean>(false);
  public messageSignal = this.messageInputService.messageSignal;
  public recordingStatus = this.messageInputService.recordingStatus;

  protected contextMenuHeaderConfig = signal<ContextMenuHeaderButton[]>([
    {
      text: 'collab_enhance_rewrite_button',
      type: GenerateTextAction.CollabRewrite,
    },
    {
      text: 'collab_enhance_concise_button',
      type: GenerateTextAction.CollabConcise,
    },
    {
      text: 'collab_enhance_bullets_button',
      type: GenerateTextAction.CollabBullets,
    },
  ]);

  protected isSendActive = computed(
    () =>
      (this.voiceTypingTextFieldHasText() || this.attachmentsSignal().fullSeries || this.attachmentsSignal().uncategorized.length > 0) &&
      !this.isGeneratingSignal(),
  );
  protected isDictationIdle = computed(() => this.transcriptionStatus() === 'idle');
  protected isEnhanceToolbarActive = computed(
    () => this.voiceTypingTextFieldHasText() && this.isDictationIdle() && !this.isGeneratingSignal(),
  );

  private dialogRef: DialogRef<CollaborationImageSelectorDialogResult> | null = null;
  protected readonly MediaType = MessageContentAttachmentMediaTypeEnum;
  protected acceptedMimeTypes = [imageMimeTypes, videoMimeTypes];
  protected lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };
  protected enhanceMessagePromptType = EnhanceMessagePromptType;

  public ngOnInit(): void {
    this.messageInputService.init();
    this.transcription.valueChanges
      .pipe(
        filter((text) => !!text),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((text) => {
        this.voiceTypingTextFieldHasText.set(!!text?.trim());
      });

    this.messageChangeSubject$.pipe(debounceTime(TOOLBAR_OPEN_DELAY), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.onEnhanceToolbarOpen();
    });
  }

  public ngOnDestroy(): void {
    this.dialogRef?.close();
    this.messageInputService.destroy();
  }

  public onSubmit(): void {
    if (!this.isSendActive()) {
      return;
    }

    this.messageInputService.setMessage(this.voiceTypingTextField.mainContent.nativeElement.innerHTML);
    this.isEnhanceToolbarOpen.set(false);
    this.cdr.detectChanges();
    this.send.emit({
      text: this.messageSignal(),
      attachments: this.attachmentsSignal(),
    });
    this.messageInputService.resetHistory();
    this.messageInputService.clearMessageInput();
    this.voiceTypingTextField.clear();
  }

  public onMessageChange(text: string): void {
    this.messageTyping.emit(true);
    this.voiceTypingTextFieldHasText.set(!!text.trim());
    this.messageInputService.handleMessageTyping({ text: text });

    if (text) {
      this.messageChangeSubject$.next(text);
    }
  }

  public onRecordingLoading(text: string): void {
    this.messageInputService.setMessage(text);
  }

  protected onFullSeriesDeleteClick(event: MouseEvent, fullSeries: FullSeriesVM): void {
    event.stopPropagation();
    const itemsToDelete = destructureFullSeries(fullSeries);

    this.messageInputService.removeAttachment(itemsToDelete);
  }

  protected onAttachmentDeleteClick(event: MouseEvent, attachments: CollabMessageAttachment[]): void {
    event.stopPropagation();
    this.messageInputService.removeAttachment(attachments);
  }

  protected onAddFromGalleryClick(): void {
    if (this.patientId()! && this.siteId()!) {
      this.dialogRef = this.dialog.open<CollaborationImageSelectorDialogResult, CollaborationImageSelectorDialogData>(
        CollaborationImageSelectorDialogComponent,
        {
          backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
          panelClass: ['w-[98%]', 'h-[87%]', '!absolute', 'top-[10%]'],
          data: {
            patientId: this.patientId()!,
            siteId: this.siteId()!,
            isPatientCollab: !!this.activatedRoute.snapshot.paramMap.get(pathParameter('patientId')),
            selectedImages: this.attachmentsSignal().allAttachments.map((attachment): ImagingGalleryItem => {
              return {
                ...attachment,
                id: attachment.id!,
                category: attachment.category,
                fullSizeUrl: attachment.url ?? attachment.thumbnailUrl ?? null,
                groupLength: 0,
              } as ImagingGalleryItem;
            }),
          },
        },
      );
      this.dialogRef.closed.subscribe((result) => {
        if (result?.attachments) {
          const removedAttachments = this.attachmentsSignal().allAttachments.filter(
            (attachment) => !result.attachments.some((addedAttachment) => attachment.id === addedAttachment.id),
          );

          const attachments = result.attachments.filter(
            (attachment) =>
              !removedAttachments.some((removeAttachment) => removeAttachment.id === attachment.id) &&
              !this.attachmentsSignal().allAttachments.some((allAttachment) => allAttachment.id === attachment.id),
          );

          if (removedAttachments.length > 0) {
            this.messageInputService.removeAttachment(removedAttachments);
          }
          if (attachments.length > 0) {
            this.messageInputService.addGalleryAttachments(attachments);
          }
        }
      });
      this.isMenuOpen.set(false);
    }
  }

  protected onUploadClick(): void {
    this.imageUploaderElement.nativeElement.click();
    this.isMenuOpen.set(false);
  }

  protected onRecordClick(): void {
    this.recordClick.emit();
  }

  protected onUpload(event: Event): void {
    const files = (event.target as HTMLInputElement).files;

    if (!files?.length) {
      return;
    }

    // eslint-disable-next-line unicorn/prefer-spread
    this.messageInputService.addAttachments(Array.from(files));
    this.imageUploaderElement.nativeElement.value = '';
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.isMenuOpen()) {
      return;
    }

    if (this.menuElement?.nativeElement?.contains(event.target as Node)) {
      // If the click is inside the menu, we let the toggle handle the open state
      return;
    }

    this.isMenuOpen.set(false);
  }

  protected onDictationClick(): void {
    if (this.recordingStatus() === 'recording' || this.recordingStatus() === 'pending') return;
    this.messageInputService.toggleSpeechRecognitionListening();

    this.tagPusherService.pushTag({
      event: TagEvent.CollaborationMessageEditing,
      enhancement: 'secure_voice_typing',
    });
  }

  protected onSelectDevice(deviceId: string): void {
    this.messageInputService.selectMicrophone(deviceId);
  }

  protected onEnhanceToolbarClick(): void {
    this.onEnhanceToolbarOpen();
  }

  protected onEnhanceMessageClick(enhanceType: EnhanceMessagePromptType): void {
    this.messageInputService.setMessage(this.voiceTypingTextField.mainContent.nativeElement.innerHTML);
    this.selectedEnhanceButton.set(enhanceType);
    this.messageInputService.enhanceMessage({ enhanceType });

    if (enhanceType === EnhanceMessagePromptType.UNBULLET) return;
    this.tagPusherService.pushTag({
      event: TagEvent.CollaborationMessageEditing,
      enhancement: this.enhancementMapping[enhanceType],
    });
  }

  protected onUndoClick(): void {
    this.messageInputService.undo();
  }

  protected onRedoClick(): void {
    this.messageInputService.redo();
  }

  public onEnhanceToolbarOpen(): void {
    this.isEnhanceToolbarOpen.set(true);
  }

  protected onEnhanceToolbarClose(): void {
    this.isEnhanceToolbarOpen.set(false);
  }

  public updateLastFocusedCursorOffset(offset: number): void {
    this.messageInputService.updateLastFocusedCursorOffset(offset);
  }

  public onGenerateTextFromContextMenu({
    action,
    text,
    textStart,
    textEnd,
  }: {
    action: GenerateTextAction;
    text: string;
    textStart: number;
    textEnd: number;
  }): void {
    this.messageInputService.enhanceMessage({
      enhanceType: this.mapEnhancePromptType(action),
      text,
      textStart,
      textEnd,
      originalMessage: this.voiceTypingTextField.mainContent.nativeElement.innerHTML,
    });
  }

  private mapEnhancePromptType(action: GenerateTextAction): EnhanceMessagePromptType {
    switch (action) {
      case GenerateTextAction.CollabBullets: {
        return EnhanceMessagePromptType.BULLETS;
      }
      case GenerateTextAction.CollabConcise: {
        return EnhanceMessagePromptType.CONCISE;
      }
      case GenerateTextAction.CollabRewrite: {
        return EnhanceMessagePromptType.REWRITE;
      }
      default: {
        return EnhanceMessagePromptType.REWRITE;
      }
    }
  }

  private readonly enhancementMapping: Record<
    Exclude<EnhanceMessagePromptType, EnhanceMessagePromptType.UNBULLET>,
    'rewrite' | 'concise' | 'bullets'
  > = {
    [EnhanceMessagePromptType.REWRITE]: 'rewrite',
    [EnhanceMessagePromptType.CONCISE]: 'concise',
    [EnhanceMessagePromptType.BULLETS]: 'bullets',
  };
}
